// src/modules/units/tyres/views/units.api.js
//
// The unit (vehicle) resource client for the tyre area.
//
// Units belong to a CUSTOMER — not to a yard and not to a tyre — so this is a
// sibling of yards.api.js and tyres.api.js rather than part of either. It follows
// the same request() envelope every caller in this module already speaks
// ({ success, status, data, message }), so a unit read handles like a yard read.
//
// WHY A TYRE-MODULE COPY, when the app already has fetchUnits() in unit-sensors?
// Because that one carries the two defects this module keeps deleting: it sends no
// `limit`, so the endpoint applies its default of 20 and SILENTLY TRUNCATES the
// fleet to twenty vehicles (the same defect yards.api.js fixed for yards), and it
// swallows a failed request into `undefined`, so a caller cannot tell an error
// from an account with no units. Reusing it would re-import both faults. This pages
// with explicit positive limits and returns failures AS failures — the module's
// standing rules.
//
// FIELD-NAME CAUTION. GET /api/customer/{id}/unit names its id `Id` (capital I)
// and its label `unitName`, and each unit also carries `imeiNumber` — the gateway
// IMEI. GET /api/tyre's documented association filter is `imeiNumber` (there is no
// `unitId` query parameter), so the imeiNumber is what a per-vehicle tyre query
// wants; the register keys its Unit filter on it for that reason.
//
// Read-only, and emits nothing: listing vehicles is neither a tyre write nor a
// scope change.

import { getUserData } from '../../../auth/views/login/auth.api.js';

const baseUrl = 'https://dashboard.pressuretrack.co.za/api';

// The DEFAULT scope root: parentCustomerId is the signed-in customer, and with
// inclChild=true the read widens to the whole hierarchy below it — so the top-level
// account sees its own vehicles AND every child customer's. Callers can override
// the root (fetchUnits({ customerId })) to re-scope to any customer in the
// hierarchy; inclChild=true then still pulls that customer PLUS its descendants,
// which is how the register lists "this yard's customer and everyone below it".
// (Same service-agent caveat as yards.api.js: an agent "acting-as" another account
// would want the active customerId surfaced through getUserData() rather than a
// global read here.)
function currentCustomerId() {
  return getUserData()?.parentCustomerId ?? null;
}

async function request(path, options = {}) {
  const { token } = getUserData() || {};

  const response = await fetch(`${baseUrl}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(options.headers || {})
    },
    credentials: 'include'
  });

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    return {
      success: false,
      status: response.status,
      message: data?.message || data?.error || 'Unit request failed.',
      data
    };
  }

  return {
    success: true,
    status: response.status,
    data
  };
}

// Explicit, positive page size. NEVER limit=0, and never "no limit" — either one
// lets the endpoint apply its own default of 20 and truncate the fleet. See the
// header note and yards.api.js for why this matters on this backend.
const UNIT_PAGE_SIZE = 100;
const UNIT_MAX_PAGES = 60;

function unitQuery({ inclChild = true, limit, offset = 0 }) {
  const q = new URLSearchParams();
  q.set('inclChild', String(inclChild));
  q.set('limit', String(limit));
  q.set('offset', String(offset));
  // The customer is the PATH root (see fetchUnits), NOT a query filter. The query
  // `customerId` this endpoint documents narrows to EXACTLY that customer and would
  // exclude its descendants — the opposite of what "customer + children" needs — so
  // it is deliberately not sent.
  //
  // No `sort` is sent either: the endpoint documents its default as `unitName`, but
  // ordering is redone on the client below where it cannot fail on an unverified
  // value — the same stance yards.api.js takes.
  return q;
}

/**
 * List a customer's units — that customer AND everyone below it in the hierarchy.
 *
 * @param {object}  [opts]
 * @param {string}  [opts.customerId]  Scope root. Defaults to the signed-in
 *   customer (the whole account). Pass a specific customer to re-scope: with
 *   inclChild=true the endpoint returns that customer plus its descendants, which
 *   is how the register lists "the selected yard's customer and its children".
 *
 * inclChild is always true, so a scope root always includes its descendants.
 *
 * Pages with explicit positive limits until a short page comes back, so the list
 * is complete rather than truncated at the endpoint's default of 20.
 *
 * A failed request is RETURNED AS A FAILURE, never as an empty list: the caller
 * paints whatever it gets, and a swallowed error would be indistinguishable from
 * an account with no vehicles — the failure mode that would make the Unit filter
 * look empty when it is really just broken.
 *
 * Results are sorted by unitName on the client for a stable, readable dropdown.
 */
export function fetchUnits(opts = {}) {
  return fetchCustomerList('unit', 'unitName', opts);
}

/**
 * List a customer's TRUCKS (customer + descendants). Each carries `Id`, `name` and
 * `IMEI` — the gateway it reports through. A tyre fitted to a truck wheel carries
 * the truck's `Id` as its `unitId`. Same paging and failure contract as fetchUnits.
 */
export function fetchTrucks(opts = {}) {
  return fetchCustomerList('truck', 'name', opts);
}

/**
 * List a customer's TRAILERS (customer + descendants). Each carries `Id`, `name`
 * and `truckIMEI` — the gateway IMEI of the truck it is CURRENTLY hitched to
 * (blank when standalone). A tyre fitted to a trailer wheel carries the trailer's
 * `Id` as its `unitId`. Same paging and failure contract as fetchUnits.
 */
export function fetchTrailers(opts = {}) {
  return fetchCustomerList('trailer', 'name', opts);
}

// The one pager behind the three customer lists above: /customer/{id}/{resource},
// paged with explicit positive limits, failures returned as failures, sorted by
// `labelKey` on the client.
async function fetchCustomerList(resource, labelKey, opts = {}) {
  const cid = opts.customerId || currentCustomerId();
  if (!cid) {
    return { success: false, status: 0, message: 'No customer in session.', data: null };
  }

  const path = `/customer/${encodeURIComponent(cid)}/${resource}`;

  // A caller that names a page gets exactly that page, untouched.
  if (Number(opts.limit) > 0) {
    return request(`${path}?${unitQuery(opts).toString()}`, { method: 'GET' });
  }

  const rows = [];
  let count = NaN;
  let status = 200;

  for (let page = 0; page < UNIT_MAX_PAGES; page++) {
    const res = await request(
      `${path}?${unitQuery({ ...opts, limit: UNIT_PAGE_SIZE, offset: rows.length }).toString()}`,
      { method: 'GET' }
    );
    if (!res.success) return res;                  // surface it; never fake an empty list
    status = res.status;
    const batch = Array.isArray(res.data?.results) ? res.data.results : [];
    count = Number(res.data?.count);
    rows.push(...batch);
    if (batch.length < UNIT_PAGE_SIZE) break;      // short page = last page
    if (Number.isFinite(count) && rows.length >= count) break;
  }

  rows.sort((a, b) =>
    String(a?.[labelKey] ?? '').localeCompare(String(b?.[labelKey] ?? ''), undefined, { numeric: true }));

  return {
    success: true,
    status,
    data: { count: Number.isFinite(count) ? count : rows.length, results: rows }
  };
}
